import React, { useState, useEffect } from 'react';
import { Instance, apiService } from '../services/apiService';

interface MonitoringProps {
  instances: Instance[];
}

const Monitoring: React.FC<MonitoringProps> = ({ instances }) => {
  const [shutdownData, setShutdownData] = useState({
    time: '12:00 AM',
    stopped_count: 0,
    duration: 'N/A'
  });
  const [refreshing, setRefreshing] = useState(false);

  const fetchShutdownLogs = async () => {
    try {
      const data = await apiService.getShutdownLogs();
      setShutdownData(data);
    } catch (error) {
      console.error('Failed to fetch shutdown logs:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchShutdownLogs();
    setTimeout(() => setRefreshing(false), 500);
  };

  useEffect(() => {
    fetchShutdownLogs();
  }, []);

  // Calculate live runtime (includes running instances)
  const calculateLiveRuntime = () => {
    const now = Date.now();
    return instances.reduce((sum, instance) => {
      let runtime = instance.RuntimeSeconds || 0;
      
      // Add live runtime for running instances
      if (instance.State === 'running' && instance.LastStartTime) {
        const runningSeconds = Math.floor((now - instance.LastStartTime * 1000) / 1000);
        runtime += runningSeconds;
      }
      
      return sum + runtime;
    }, 0) / 3600;
  };

  const runningInstances = instances.filter(i => i.State === 'running');
  const stoppedInstances = instances.filter(i => i.State === 'stopped');
  const criticalQuota = instances.filter(i => ((i.RuntimeSeconds || 0) / 3600) / (i.QuotaHours || 30) > 0.9);
  const warningQuota = instances.filter(i => {
    const pct = ((i.RuntimeSeconds || 0) / 3600) / (i.QuotaHours || 30);
    return pct > 0.8 && pct <= 0.9;
  });
  const expiredInstances = instances.filter(i => i.Expired);
  const totalRuntime = calculateLiveRuntime();
  const avgQuotaUsage = instances.length > 0 
    ? instances.reduce((sum, i) => sum + (((i.RuntimeSeconds || 0) / 3600) / (i.QuotaHours || 30) * 100), 0) / instances.length 
    : 0;

  return (
    <div>
      <div className="content-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>📊 System Monitoring Dashboard</h2>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            padding: '0.75rem 1.5rem',
            background: refreshing ? '#9ca3af' : 'linear-gradient(135deg, #0066CC, #4A90E2)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            if (!refreshing) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 102, 204, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 102, 204, 0.3)';
          }}
        >
          <span style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Critical Alerts */}
      {(criticalQuota.length > 0 || expiredInstances.length > 0) && (
        <div style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)', border: '2px solid #ef4444', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🚨</span>
            <strong style={{ color: '#991b1b' }}>Critical Alerts</strong>
          </div>
          {criticalQuota.length > 0 && (
            <div style={{ color: '#7f1d1d', fontSize: '0.9rem' }}>• {criticalQuota.length} instance(s) over 90% quota</div>
          )}
          {expiredInstances.length > 0 && (
            <div style={{ color: '#7f1d1d', fontSize: '0.9rem' }}>• {expiredInstances.length} instance(s) quota expired</div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', borderRadius: '12px', padding: '1.25rem', color: 'white', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>⚠️ Critical</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{criticalQuota.length}</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.5rem' }}>&gt;90% quota</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: '12px', padding: '1.25rem', color: 'white', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>⚡ Warning</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{warningQuota.length}</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.5rem' }}>80-90% quota</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', borderRadius: '12px', padding: '1.25rem', color: 'white', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>⏱️ Total Runtime</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
            {Math.floor(totalRuntime)}h {Math.floor((totalRuntime % 1) * 60)}m
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.5rem' }}>All instances</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: '12px', padding: '1.25rem', color: 'white', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>📊 Avg Quota</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{avgQuotaUsage.toFixed(0)}%</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.5rem' }}>Usage rate</div>
        </div>
      </div>

      {/* Auto-Shutdown & Last Run */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ background: 'rgba(255, 255, 255, 0.9)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
            🌙 Auto-Shutdown
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.9rem', color: '#166534' }}>Status</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#15803d' }}>✅ ENABLED</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#eff6ff', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.9rem', color: '#1e40af' }}>Schedule</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e3a8a' }}>12:00 AM AEST</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.9rem', color: '#92400e' }}>Next Run</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#78350f' }}>Tonight</span>
            </div>
          </div>
        </div>

        <div style={{ background: 'rgba(255, 255, 255, 0.9)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
            📜 Last Shutdown
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.9rem', color: '#166534' }}>Time</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#15803d' }}>{shutdownData.time}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#eff6ff', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.9rem', color: '#1e40af' }}>Stopped</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e3a8a' }}>{shutdownData.stopped_count} instances</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.9rem', color: '#92400e' }}>Duration</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#78350f' }}>{shutdownData.duration}</span>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Monitoring;
